import { useAppDispatch, useAppSelector } from '@hooks';

import { useDeleteCustomerMutation } from '@api/customer.api';
import { TextCodes } from '@api/types/translation/TextCodes';
import { Button, Text, Title } from '@components';
import { Close } from '@mui/icons-material';
import { Alert, Box, Modal, styled } from '@mui/material';
import { getModalState, setModalState } from '@store/modal/modal.slice';
import { GlobalStyles } from '@themes/global/GlobalStyles';
import { t } from '@translations';
import { useDealerDetailsContext } from './DealerDetailsProvider';

const ModalContentWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  padding: '16px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.primary.main}`,
  minWidth: '400px',
  maxHeight: '80vh',
  overflow: 'auto',
}));

export const DeleteCustomerModal = () => {
  const { deleteUser, deleteModalId, deleteUserEmail, toggleEditDataState } =
    useDealerDetailsContext();
  const dispatch = useAppDispatch();
  const modalState = useAppSelector(getModalState(deleteModalId));
  const [trigger, status] = useDeleteCustomerMutation();
  const { isLoading, isError, error } = status;
  const isWorking = isLoading;
  const errorMessage = (error as any)?.data?.data?.message;

  const closeHandler = () => {
    dispatch(setModalState({ id: deleteModalId, isVisible: false }));
  };

  const deleteUserHandler = async () => {
    const encodedEmail = deleteUserEmail ? encodeURIComponent(deleteUserEmail) : null;
    try {
      await trigger(encodedEmail as string).unwrap();
      deleteUser();
      closeHandler();
      toggleEditDataState(false);
    } catch (error) {
      console.error(error);
    }
  };
  return (
    <Modal
      open={modalState.isVisible}
      onClose={closeHandler}
      aria-labelledby='delete-user-modal'
      id={deleteModalId}
    >
      <GlobalStyles>
        <ModalContentWrapper>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title variant='h5'>{t(TextCodes.Dealer.Details.DeleteUser)}</Title>
            <Close onClick={closeHandler} sx={{ cursor: 'pointer' }} />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Text
              dangerouslySetInnerHTML={{
                __html: t(TextCodes.Dealer.Details.DeleteUserConfirmation, {
                  email: `<strong>${deleteUserEmail}</strong>` || '',
                }),
              }}
            ></Text>
          </Box>
          {isError && errorMessage ? (
            <Alert severity='error' sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          ) : null}
          <Button onClick={deleteUserHandler} busy={isWorking} variant='secondary' sx={{ mt: 2 }}>
            {t(TextCodes.Base.Delete)}
          </Button>
        </ModalContentWrapper>
      </GlobalStyles>
    </Modal>
  );
};
